/**
 * usePersonalCreditLow — decides whether the header "Buy more credits" CTA
 * should show, based on the caller's PERSONAL account credit balance.
 *
 * Scope: personal (individual) credits only. The personal account is the
 * billable account with `organisationId == null` (resolved via
 * `resolveScopedAccount`). Organisation balances are intentionally ignored —
 * the CTA reflects the user's own wallet regardless of which account is
 * currently active for charging.
 *
 * Which balance figure: keys off `balance.total` (credits OWNED), NOT
 * `balance.available`. Per the DB source of truth (`_credit.balance_components`):
 *   total     = sum of open active in-period unexpired grant balances
 *   reserved  = credits held for in-flight jobs
 *   available = total − reserved   (signed; can go negative after a clawback)
 *   frozen    = dispute-held grants (excluded from both)
 * `available` moves every time a job reserves/releases credits, so keying the
 * CTA off it would flicker the button on/off during normal use with no real
 * spend. `total` only changes on genuine mint/settle/expiry/clawback — the
 * correct "am I running low?" signal.
 *
 * Anti-flicker: asymmetric thresholds (hysteresis). The CTA appears once owned
 * credits drop below ENTER, and only disappears again once they climb back
 * above EXIT. This prevents on/off oscillation at the boundary even during real
 * spend (a top-up is 100k minimum, so it clears EXIT comfortably).
 *
 * Live: `useBillableAccounts` refetches on every credit-activity WS event (see
 * useUnifiedEventConsumer) plus a 60s backstop, so this re-evaluates itself.
 */
import { useState } from "react";
import { useBillableAccounts } from "@/hooks/useBillableAccounts";
import { resolveScopedAccount } from "@/components/settings/billing/accountScope";
import type { BillableAccount } from "@/services/billingApi";

/** Owned-credit level (personal) at/below which the CTA appears. */
export const LOW_BALANCE_ENTER_CREDITS = 10_000;
/** Owned-credit level the balance must exceed for the CTA to disappear again. */
export const LOW_BALANCE_EXIT_CREDITS = 12_000;

export interface PersonalCreditLowState {
  /** True while the "Buy more credits" CTA should be shown. */
  isLow: boolean;
  /** The resolved personal account, or null if none / not yet loaded. */
  personalAccount: BillableAccount | null;
  /** True while the underlying accounts query is loading. */
  isLoading: boolean;
}

export function usePersonalCreditLow(): PersonalCreditLowState {
  const { accounts, isLoading } = useBillableAccounts();

  const personalAccount = accounts.length
    ? resolveScopedAccount(accounts, { kind: "personal" })
    : null;

  // Owned credits (see file header for why `total`, not `available`).
  const owned = personalAccount?.balance.total;

  // Hysteresis needs memory of the previous output, so we hold the shown/hidden
  // state — but derive the next value DURING render (React's "adjusting state on
  // prop change" pattern) rather than in an effect, so the CTA is correct on the
  // first commit with no extra render or one-frame flash.
  const [shown, setShown] = useState(false);

  let isLow = shown;
  if (!isLoading) {
    isLow =
      owned == null
        ? false // no personal account resolved → never low (clears any stale true)
        : shown
          ? owned < LOW_BALANCE_EXIT_CREDITS // currently shown: hide only above exit
          : owned < LOW_BALANCE_ENTER_CREDITS; // currently hidden: show below enter
  }
  if (isLow !== shown) setShown(isLow);

  return { isLow, personalAccount, isLoading };
}
